import { z } from 'zod';
import { InteractionAction } from '../enums.js';
import { IdempotencyKeySchema, IsoDateTimeSchema, UuidSchema } from '../primitives.js';

/**
 * Interaction record. Stored in DynamoDB keyed by (actorUserId, targetUserId).
 * `action` is the latest terminal state; `history` is the audit trail of all
 * prior actions in the pair.
 */
export const InteractionSchema = z.object({
  actorUserId: UuidSchema,
  targetUserId: UuidSchema,
  action: InteractionAction,
  history: z
    .array(
      z.object({
        action: InteractionAction,
        ts: IsoDateTimeSchema,
      }),
    )
    .max(50),
  lastActionTs: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
});
export type Interaction = z.infer<typeof InteractionSchema>;

/** Request payload for actions a viewer can take on a candidate. */
export const InteractionRequestSchema = z.object({
  targetUserId: UuidSchema,
  action: z.enum(['pass', 'like', 'approach']),
  /** Required for `approach`; chat composer pre-populates message. */
  approachMessage: z.string().trim().min(1).max(500).optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type InteractionRequest = z.infer<typeof InteractionRequestSchema>;

/** Request payload for actions a recipient can take on an incoming approach. */
export const InboxActionRequestSchema = z.object({
  fromUserId: UuidSchema,
  action: z.enum(['ignore', 'dismiss', 'block', 'like']),
  idempotencyKey: IdempotencyKeySchema,
});
export type InboxActionRequest = z.infer<typeof InboxActionRequestSchema>;

/** Outcome of an approach attempt. Drives free-vs-paid retry rules. */
export const ApproachAttemptSchema = z.object({
  pairKey: z.string(), // `${minUserId}#${maxUserId}` — direction-independent
  senderUserId: UuidSchema,
  receiverUserId: UuidSchema,
  paid: z.boolean(),
  creditsSpent: z.number().int().min(0).max(1),
  outcome: z.enum(['pending', 'ignored', 'dismissed', 'blocked', 'liked_back']),
  outcomeAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type ApproachAttempt = z.infer<typeof ApproachAttemptSchema>;
